/* Everything that writes into the HubSpot embed on the fit check modal.

   SPLIT FROM assets/fx-fitpart.js ON PURPOSE. That file owns the picker UI and
   publishes the chosen parts on window.fxFitSelection; this one owns the
   fields. A field rename cannot break the UI and a layout change cannot break
   the writing, and neither file grows past the size a theme asset write
   handles comfortably.

   REACT OWNS THOSE INPUTS. Assigning input.value directly does not register
   with React and the value will not submit. It has to go through the native
   prototype setter and then dispatch input and change. This is the single
   thing most likely to be "simplified" later and silently break the feature,
   which is why setField is the only place allowed to touch a value.

   THE TICKET IS BUILT HERE, NOT IN A WORKFLOW. The form carries Ticket name
   and Ticket description as hidden ticket properties, so the submission
   creates the ticket itself with the right name and a readable description.
   That is why there is no naming workflow: composing it at source avoids the
   two minute delay and avoids a second ticket. Action 4 was deleted from
   workflow 1859650199 on 22 Oct for exactly that reason; its other two
   branches still create their own tickets.

   NEVER MAKE THE PICKER CONDITIONAL ON A WRITE SUCCEEDING. If the fields are
   missing or renamed, setField returns false, the customer still gets a
   working form, and only the formatting is lost. The message they type is a
   real HubSpot field in its own right for exactly that reason.

   NAMES FOLLOW THE HOUSE CONVENTION, which is contact name first then what it
   is: "First Last | Warranty Claim", "Name | Voicemail", and T0: Final ticket
   naming uses contact name plus category. So: "Jon Jones | Parts Store |
   Compatibility Check". */
(function () {
  'use strict';

  var NL = String.fromCharCode(10);
  var queued = null;

  /* The dialog is shared between the backorder form and the fit check, so
     what the ticket should be called is read from the title the extension sets
     rather than assumed from the page.

     SEVERAL SPELLINGS PER KIND BECAUSE THE WORDING MOVED. Jonnie asked for
     "Notify me" to become "Request Backorder" on 27 Oct, since there was no
     way to match a notify request back to a part and the word promised
     something the store could not do. The form, the button and the extension's
     dialog title are three separate places and they will not all change on the
     same day, so every spelling any of them has used maps to the same kind.
     The fit check patterns are tested first: "Check it fits before you order"
     contains neither stock nor backorder, but a future rewording might. */
  var KINDS = [
    { has: 'fits', kind: 'Compatibility Check', line: 'Parts compatibility check' },
    { has: 'compat', kind: 'Compatibility Check', line: 'Parts compatibility check' },
    { has: 'backorder', kind: 'Backorder', line: 'Backorder request, part is out of stock' },
    { has: 'back order', kind: 'Backorder', line: 'Backorder request, part is out of stock' },
    { has: 'notif', kind: 'Backorder', line: 'Backorder request, part is out of stock' },
    { has: 'stock', kind: 'Backorder', line: 'Backorder request, part is out of stock' }
  ];


  /* ------------------------------------------------------------- hosts -- */

  /* THREE FORMS, TWO SHELLS, ONE PICKER. On a part page the fit check and the
     backorder request share the extension's .vcp-form dialog. On /pages/support
     the parts request form is an ordinary embed that sections/fx-support.liquid
     mounts into #fxSupportForm, with no dialog, no extension title and a
     heading the page has already drawn.

     Everything that differs between the two shells is answered here, so the
     rest of the file never asks which page it is on. Read fresh every pass:
     the dialog is created and destroyed as it opens and closes. */
  function host() {
    var dialog = document.querySelector('.vcp-form__dialog');
    if (dialog) {
      var mount = dialog.querySelector('.vcp-form__mount');
      var sub = dialog.querySelector('.vcp-form__sub');
      if (mount && sub) {
        return { shell: 'modal', mount: mount, after: sub, before: null, heading: true };
      }
    }
    var sup = document.getElementById('fxSupportForm');
    if (sup && sup.parentNode) {
      return { shell: 'support', mount: sup, after: null, before: sup, heading: false };
    }
    return null;
  }

  function frameDoc() {
    try {
      var h = host();
      var f = h && h.mount && h.mount.querySelector('iframe');
      return (f && f.contentDocument) || null;
    } catch (e) {
      return null;
    }
  }

  /* HUBSPOT NAMESPACES TICKET PROPERTIES ON THE RENDERED FORM. A contact field
     comes through as name="message", but a ticket field comes through as
     name="TICKET.subject". Nothing in the form editor says so; the only way to
     find out is to read the rendered input, and the first build of this file
     wrote to "subject" and "content" and silently hit nothing at all. Every
     lookup tries the namespaced name first and falls back to the plain one, so
     the same code works whether a property is wired to the ticket or the
     contact, and keeps working if that is ever switched.

     THIS IS WHY part_in_question NEEDED NO CODE CHANGE when the ticket level
     copies of it were created on 27 Oct. The contact properties already
     existed; adding ticket properties with the same internal names means the
     TICKET. lookup starts hitting the moment the hidden fields are added to
     the form. See fieldEls() below for the case where both are on one form. */
  function fieldEl(name) {
    var doc = frameDoc();
    if (!doc) return null;
    return doc.querySelector('[name="TICKET.' + name + '"]') ||
      doc.querySelector('[name="' + name + '"]');
  }

  /* AND A FORM MAY CARRY BOTH COPIES AT ONCE (28 Oct). part_in_question now
     exists as a contact property and as a ticket property with the same
     internal name, and the backorder form carries both: the contact copy so
     the value lands on the person's record, the ticket copy so it lands on a
     property the parts team can see on the ticket card and filter a view by.

     fieldEl() answers with one element and has to keep preferring the ticket
     copy, because that is the one whose type decides whether a line break
     survives. Writing, though, has to reach every copy, or the one it skips
     submits blank. This returns them all, in no particular order. */
  function fieldEls(name) {
    var doc = frameDoc();
    if (!doc) return [];
    var out = [];
    var a = doc.querySelector('[name="TICKET.' + name + '"]');
    var b = doc.querySelector('[name="' + name + '"]');
    if (a) out.push(a);
    if (b && b !== a) out.push(b);
    return out;
  }

  /* The only place allowed to set a value on a HubSpot input. See the header. */
  function setField(name, value) {
    try {
      var els = fieldEls(name);
      if (!els.length) return false;
      var hit = false;
      for (var i = 0; i < els.length; i++) {
        var el = els[i];
        if (el.value === value) { hit = true; continue; }
        var proto =
          el.tagName === 'TEXTAREA' ? window.HTMLTextAreaElement.prototype :
          el.tagName === 'SELECT' ? window.HTMLSelectElement.prototype :
          window.HTMLInputElement.prototype;
        var desc = Object.getOwnPropertyDescriptor(proto, 'value');
        if (!desc || !desc.set) continue;
        desc.set.call(el, value);
        el.dispatchEvent(new Event('input', { bubbles: true }));
        el.dispatchEvent(new Event('change', { bubbles: true }));
        hit = true;
      }
      return hit;
    } catch (e) {
      return false;
    }
  }

  function readField(name) {
    try {
      var el = fieldEl(name);
      return el ? String(el.value || '').trim() : '';
    } catch (e) {
      return '';
    }
  }

  /* True when a field can hold line breaks. A hidden input can, a textarea
     can, a visible single line input cannot. See the long note in compose(). */
  function multiline(name) {
    var el = fieldEl(name);
    return !el || el.tagName === 'TEXTAREA' || el.type === 'hidden';
  }

  /* --------------------------------------------------------- the embed -- */

  /* THE MODAL HAS NO VISIBLE HEADING OF ITS OWN. .vcp-form__title measures one
     pixel high, so the extension is using it as a screen reader label. The
     picker draws the real heading, which is why both rich text fieldsets can
     go: the logo, and the heading with its "Please enter the form below"
     filler. Matched on :has() and not :first-of-type so a rule stops matching
     when its module is deleted in HubSpot instead of sliding onto whatever
     moved up into first place.

     TWO SHAPES, BECAUSE HUBSPOT USES TWO (29 Oct). On the fit check form the
     rich text modules come wrapped in fieldset.form-columns-0. On the
     backorder form they do not: the logo and the heading are bare
     div.hs-richtext inside an ordinary div, so the fieldset rules matched
     nothing and the dialog carried the picker's heading and the form's own
     heading one under the other. The .hs-richtext rules catch both renderings
     and leave the legal consent block alone, since that one holds a p rather
     than an h1 or an img.

     No !important on the new pair. dress() moves this stylesheet to the end
     of the frame's head, so at equal specificity it already wins on source
     order. Verified in the live frame before it was written: the two headings
     went, the consent paragraph stayed. */
  var TRIM_CSS =
    'fieldset.form-columns-0:has(img) { display: none !important; }' +
    'fieldset.form-columns-0:has(h1) { display: none !important; }' +
    '.hs-richtext:has(img) { display: none; }' +
    '.hs-richtext:has(h1) { display: none; }';

  /* DIRECTION B, THE FILLED TREATMENT. The picker above the form uses filled
     grey chips on a hairline, so the fields take the same fill, the same
     radius and the same hairline and the whole modal reads as one surface
     rather than a picker sitting on top of somebody else's form.

     THIS IS SCOPED TO THIS MODAL ON PURPOSE. It is not in assets/fx-hsdress
     .js, which is the shared baseline for every HubSpot embed on the store:
     the support sheet and the dealer request form use that one, and the
     dealer request block carries its own inlined copy of it. Changing the
     baseline would restyle all of them from here, unseen.

     The label rule repeats fx-hsdress.js's shape rather than simplifying it.
     HubSpot emits a per-render rule carrying a GUID that changes every
     render, so a plain `.hs-form-field > label` loses to it; matching its
     shape ties on specificity and wins on source order, because this style
     tag is appended last. Symptom if someone "tidies" it: font-weight
     applies and font-size does not.

     THE VIN RULE REPEATS .hs-input AND THAT IS THE WHOLE POINT. vin_number is
     a multi line text property in HubSpot, so the form renders it as a
     textarea and a five digit number gets a four line box. The property is
     shared with the warranty and registration forms, which other people own,
     so rather than change its field type account wide it is pinned to one
     line here. The suffix match catches vin_number, trailer_vin_number and
     the TICKET. namespaced spellings of both.

     The doubled class is load bearing. :not() counts its argument towards
     specificity, so the padding rule at the top of this block scores 0,3,0
     (one class, two attributes) while a plain
     textarea.hs-input[name$="vin_number"] scores only 0,2,1 and loses to it,
     no matter that it comes later. Measured on the live form: the selector
     matched, the rule was last in the sheet, and the computed padding stayed
     at 14px. Repeating .hs-input takes it to 0,3,1 and it wins.

     height is auto rather than a number, with rows set to 1 by slimVin()
     below. 8px of padding either side of one 22.4px line lands at 40px, which
     is exactly what the text inputs on this form measure, and letting it size
     itself means a long value grows the box instead of being clipped. */
  var SKIN_CSS =
    '.hs-input:not([type="checkbox"]):not([type="radio"]) { padding: 14px 15px; background: #F7F7F7; border-color: #E5E5E5; }' +
    '.hs-input:not([type="checkbox"]):not([type="radio"]):focus { background: #FFFFFF; border-color: #1D1D1F; }' +
    '.hs-form .hs-form-field > label:not(.hs-error-msg) { font-size: 15px; }' +
    '.hs-form-field { margin-bottom: 18px; }' +
    'textarea.hs-input { min-height: 96px; }' +
    '.hs-button.primary, input[type="submit"].hs-button { width: 100%; max-width: none; }' +
    'textarea.hs-input.hs-input[name$="vin_number"] { min-height: 0; height: auto; padding: 8px 15px; resize: none; }';

  /* OURS HAS TO BE THE LAST STYLESHEET IN THAT HEAD, AND IT IS NOT BY DEFAULT.
     Read the frame's head and the order is: fxHsDress, fxFitTrim, then
     HubSpot's own per render stylesheet, then #vinFxHsEmbedStyle, which is
     the app extension injecting its own copy of embed styling. Both of those
     land after ours. At equal specificity the last one wins, so the fields
     kept HubSpot's white and the extension's border no matter what we wrote,
     while the label rule happened to win because nothing after it set a label
     size. That is the kind of thing that reads as "the CSS did not apply".

     So the style element is MOVED to the end of the head whenever something
     appends after it, rather than reaching for !important, which would be a
     fight nobody can win twice. appendChild on a node already in the document
     moves it; once nothing lands after it this is a no-op. */
  function dress() {
    try {
      var h = host();
      if (!h) return;
      if (typeof window.fxDressHsFrame === 'function') window.fxDressHsFrame(h.mount);
      var doc = frameDoc();
      if (!doc || !doc.head) return;
      var st = doc.getElementById('fxFitTrim');
      if (!st) {
        st = doc.createElement('style');
        st.id = 'fxFitTrim';
        st.textContent = TRIM_CSS + SKIN_CSS;
      }
      if (doc.head.lastElementChild !== st) doc.head.appendChild(st);
    } catch (e) {
      /* Cross origin or torn down mid pass. An unstyled form that submits
         beats a styled one that never appears. */
    }
  }

  /* THE CSS ABOVE IS THE GUARD, THIS IS THE FIX. rows is what actually drives
     a textarea's natural height, so setting it to 1 makes the box the right
     size even in the moment before our stylesheet has been moved back to the
     end of the head. Belt and braces, because the VIN sits above the fold and
     a four line box there is the first thing anyone notices. */
  function slimVin() {
    try {
      var el = fieldEl('trailer_vin_number') || fieldEl('vin_number');
      if (!el || el.tagName !== 'TEXTAREA') return;
      if (el.rows !== 1) el.rows = 1;
    } catch (e) {
      /* No VIN field on this form. Nothing to slim. */
    }
  }

  /* ------------------------------------------------------- what we know -- */

  function knownVin() {
    try {
      var raw = window.localStorage.getItem('lastSearchedTrailer');
      var rec = raw ? JSON.parse(raw) : null;
      return rec ? String(rec.vinNumber || '').trim() : '';
    } catch (e) {
      return '';
    }
  }

  function kindNow() {
    /* The support page has no dialog and therefore no title to read, so the
       shell itself names the kind. */
    var h = host();
    if (h && h.shell === 'support') {
      return { kind: 'Parts Request', line: 'Parts request, could not find the part' };
    }
    var t = document.querySelector('.vcp-form__title');
    var s = (t ? t.textContent : '').toLowerCase();
    for (var i = 0; i < KINDS.length; i++) {
      if (s.indexOf(KINDS[i].has) > -1) return KINDS[i];
    }
    return { kind: 'Enquiry', line: 'Parts enquiry' };
  }

  function label(p) {
    return p.sku ? p.sku + ' - ' + p.title : p.title;
  }

  function absolute(url) {
    try {
      return new URL(url, window.location.origin).href;
    } catch (e) {
      return url || '';
    }
  }

  /* ---------------------------------------------------------- composing -- */

  /* THE VIN IS PREFILLED ONCE, NOT ENFORCED. It is a visible field so the
     customer can correct it, and rewriting it on every pass would undo their
     correction as they typed. Only fill an empty one. It stays empty when the
     trailer was picked by model rather than searched by VIN, because then
     there is no VIN to fill and inventing one would be worse than blank. */
  var vinDone = false;

  function fillVin() {
    if (vinDone) return;
    var vin = knownVin();
    if (!vin) return;
    if (readField('trailer_vin_number')) { vinDone = true; return; }
    if (setField('trailer_vin_number', vin) || setField('vin_number', vin)) vinDone = true;
  }

  function compose() {
    var parts = window.fxFitSelection || [];
    var k = kindNow();

    var first = readField('firstname');
    var last = readField('lastname');
    var who = (first + ' ' + last).trim();

    setField('subject', (who ? who + ' | ' : '') + 'Parts Store | ' + k.kind);

    var lines = [k.line, ''];

    if (parts.length) {
      lines.push(parts.length === 1 ? 'Part in question' : 'Parts in question (' + parts.length + ')');
      for (var i = 0; i < parts.length; i++) {
        lines.push((i + 1) + '. ' + label(parts[i]));
        if (parts[i].url) lines.push('   ' + absolute(parts[i].url));
      }
      lines.push('');
    }

    var vin = readField('trailer_vin_number') || readField('vin_number') || knownVin();
    if (vin) {
      lines.push('Trailer VIN: ' + vin);
      lines.push('');
    }

    var msg = readField('message');
    if (msg) {
      lines.push('Message');
      lines.push(msg);
    }

    /* A VISIBLE TEXT INPUT STRIPS LINE BREAKS OUT OF ITS VALUE, and it does
       not replace them with spaces, it removes them, so a multi line
       description written into one collapses into a run on sentence.

       A HIDDEN input does NOT: the value sanitisation algorithm that strips
       line breaks applies to text, search, tel, url, email and password, not
       to hidden. Verified on the live form, three lines in and three lines
       back out. This matters because HubSpot renders EVERY hidden field as
       input type=hidden whatever the property type is, so checking for a
       TEXTAREA alone wrongly concluded the field could not hold line breaks
       and fell back to a separator for no reason.

       The fallback stays for the case it was written for: someone unhides the
       field and it becomes a real single line input. */
    while (lines.length && !lines[lines.length - 1]) lines.pop();
    var multi = multiline('content');
    var out = multi ? lines : lines.filter(function (l) { return l.trim(); });
    setField('content', out.join(multi ? NL : ' | '));

    /* THE STRUCTURED COPY, now on the ticket as well as the contact. Part in
       question was created as a multi line ticket property on 27 Oct so that
       several parts read as a list on the record rather than a run of
       semicolons, which is why this follows the same multiline test as the
       description above rather than always joining with "; ".

       The ticket description is what a human reads. This is what a filter
       reads: "Part in question contains ASM-BEARING-KIT" is how the parts
       team finds every open backorder for a part when stock lands. */
    setField('part_in_question', parts.map(label).join(multiline('part_in_question') ? NL : '; '));
    setField('part_in_question_url', parts.map(function (p) {
      return absolute(p.url);
    }).join(' '));
  }

  /* TYPING INSIDE THE IFRAME DOES NOT MUTATE THIS DOCUMENT, so the observer
     below never sees it and the description would freeze at whatever the
     fields held when the frame first appeared. The inputs have to be listened
     to directly, once per frame document. */
  function wire() {
    var doc = frameDoc();
    if (!doc || doc.fxpWired) return;
    var names = ['firstname', 'lastname', 'message', 'trailer_vin_number', 'vin_number'];
    var found = 0;
    for (var i = 0; i < names.length; i++) {
      var el = fieldEl(names[i]);
      if (!el) continue;
      found++;
      el.addEventListener('input', compose);
      el.addEventListener('change', compose);
    }
    if (found) doc.fxpWired = true;
  }

  function pass() {
    queued = null;
    try { dress(); } catch (e) { /* frame not ready */ }
    try { slimVin(); } catch (e) { /* no vin field yet */ }
    try { wire(); } catch (e) { /* frame not ready */ }
    try { fillVin(); } catch (e) { /* no vin field yet */ }
    try { compose(); } catch (e) { /* nothing to write into */ }
  }

  function schedule() {
    if (queued) return;
    queued = window.setTimeout(pass, 60);
  }

  function boot() {
    pass();
    document.addEventListener('fx:fitparts', schedule);
    if (!window.MutationObserver) return;
    new window.MutationObserver(schedule).observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
